import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  autoAssignPageQuery,
  saveAutoAssign,
  type AutoAssignFields,
  type AutoAssignTeam,
} from '../../api/settingsEaseedeskAutoAssign'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Auto-assign settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/auto_assign/
 * index.php`, si gest cu gest fata de `index-1.js`: lamele care se deschid din
 * NUME (nu din tot randul - altfel fiecare pornire de repartizare ar deschide
 * si panoul), comutatorul pe lamela la dreapta, un singur formular pentru tot
 * si un buton "Update settings" sus.
 *
 * Setarile fiecarei echipe stau stranse sub lamela ei: un furnizor poate avea
 * douazeci de echipe, iar desfacute toate ecranul ajunge de zece pagini.
 *
 * Toate propozitiile de sub campuri sunt cele din macheta veche, cuvant cu
 * cuvant. Nu sunt umplutura: fiecare raspunde la o intrebare pe care ecranul o
 * ridica si n-o poate raspunde altfel - unde e comutatorul de pe cont, ce se
 * intampla cand nimeni nu califica, si ca preferinta de sediu e o PRIORITATE,
 * nu un filtru.
 */
export function AutoAssignPage() {
  const queryClient = useQueryClient()
  const page = useQuery(autoAssignPageQuery())

  /* Ce s-a schimbat pe ecran, pe echipa. Ecranul are un singur buton, deci se
     tine local pana la apasare - ca in formularul vechi. */
  const [draft, setDraft] = useState<Record<number, AutoAssignFields>>({})
  const [open, setOpen] = useState<Record<number, boolean>>({})

  useEffect(() => {
    setDraft({})
  }, [page.data])

  const save = useMutation({
    mutationFn: () => saveAutoAssign(draft),
    onSuccess: (answer) => {
      toast(`${answer.saved ?? 0} team(s) saved.`, 'Success', 'success')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'auto_assign'] })
    },
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  /* Cererea care pica lasa `data` pe `undefined` pentru totdeauna, si spinnerul
     se invartea fara capat. Motivul si Retry, ca pe Support Licenses. */
  if (page.error) return <LoadFailed what="Auto-assign settings" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const { teams, strategies, can_edit: canEdit } = page.data

  const valueOf = (team: AutoAssignTeam): AutoAssignFields =>
    draft[team.group_id] ?? {
      enabled: team.enabled,
      strategy: team.strategy,
      only_online: team.only_online,
      online_minutes: team.online_minutes,
      respect_work_hours: team.respect_work_hours,
      require_optin: team.require_optin,
      prefer_office_responsible: team.prefer_office_responsible,
    }

  const change = (team: AutoAssignTeam, patch: Partial<AutoAssignFields>) =>
    setDraft((was) => ({ ...was, [team.group_id]: { ...valueOf(team), ...patch } }))

  return (
    <div>
      <SettingsHead
        strong="Auto-assign"
        rest="settings"
        subtitle={
          <>
            Who in a <strong>team</strong> picks up a new ticket
          </>
        }
      />

      {teams.length === 0 ? (
        <span className="help-block">
          There are no teams yet. Create one in Settings &rsaquo; User accounts &rsaquo; Account groups.
        </span>
      ) : (
        <>
          <div className="row" style={{ marginBottom: 20 }}>
            <div className="col-md-12">
              <button
                type="button"
                className="btn btn-primary pull-right update-autoassign-settings"
                disabled={!canEdit || save.isPending || Object.keys(draft).length === 0}
                onClick={() => save.mutate()}
              >
                Update settings
              </button>
            </div>
          </div>

          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {teams.map((team) => {
              const value = valueOf(team)
              const shown = open[team.group_id] === true

              return (
                <li key={team.group_id} className="dispatching-list">
                  <div className="row">
                    <div className="col-md-5">
                      <h5
                        style={{ margin: 0, padding: '5px 0', cursor: 'pointer' }}
                        onClick={() => setOpen((was) => ({ ...was, [team.group_id]: !shown }))}
                      >
                        <i
                          className={`fas ${shown ? 'fa-angle-up' : 'fa-angle-down'} color-blue font-14 aaCaret`}
                          aria-hidden="true"
                        ></i>{' '}
                        {team.group_name}
                      </h5>
                    </div>

                    <div className="col-md-5">
                      <span className="help-block" style={{ margin: '6px 0 0' }}>
                        {team.members} member{team.members === 1 ? '' : 's'}
                        {team.members < 2 ? ' — nothing to spread' : ''}
                        {value.require_optin === 1 && team.members > 0 ? (
                          <>
                            <br />
                            {team.opted_in < 1 ? (
                              <>
                                <span className="text-danger">
                                  Nobody here accepts automatic tickets yet, so nothing will be assigned.
                                </span>{' '}
                                Turn it on per person in Settings &rsaquo; Users &rsaquo; the account &rsaquo; Privacy
                                settings, as <em>Auto assign</em> — or untick the condition below.
                              </>
                            ) : (
                              <>
                                {team.opted_in} of them accept{team.opted_in === 1 ? 's' : ''} automatic tickets.
                              </>
                            )}
                          </>
                        ) : null}
                      </span>
                    </div>

                    <div className="col-md-2">
                      <div className="pull-right">
                        {/*
                         * Era carligul lui Switchery - plugin pe care v4 nu-l
                         * incarca si care n-are nicio regula in foile servite.
                         * Iesea o casuta goala de 15px plutita la dreapta, fara
                         * nimic langa ea care sa spuna ce comuta. Acum e bifa
                         * zonei (`od-inv-check`, ui/SettingsParts.css), lipita
                         * de cuvantul ei.
                         *
                         * Scrisa aici si nu cu `SettingsCheck`: piesa comuna
                         * n-are `disabled`, iar ecranul se arata si cuiva care
                         * are `.read` fara `.edit`. Clasa ramane a ei.
                         */}
                        <label className="od-inv-check">
                          <input
                            type="checkbox"
                            name={`teams[${team.group_id}][enabled]`}
                            disabled={!canEdit}
                            checked={value.enabled === 1}
                            onChange={(e) => change(team, { enabled: e.target.checked ? 1 : 0 })}
                          />
                          <span>Auto-assign</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {shown ? (
                    <div>
                      <hr className="dotted" />

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label className="control-label">
                              Choose the person with{' '}
                              <i
                                className="fas fa-info-circle color-blue font-14"
                                style={{ cursor: 'default' }}
                                title="Only open tickets count. What somebody closed last month is not a load."
                              ></i>
                            </label>
                            <select
                              className="form-control"
                              disabled={!canEdit}
                              value={String(value.strategy)}
                              onChange={(e) => change(team, { strategy: Number(e.target.value) })}
                            >
                              {strategies.map((one) => (
                                <option key={one.value} value={one.value}>
                                  {one.label}
                                </option>
                              ))}
                            </select>
                            <span className="help-block">
                              If nobody qualifies, the ticket stays with the team and somebody picks it up from the
                              queue.
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <label className="control-label">Who is eligible</label>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <div className="checkbox">
                            <label>
                              <input
                                type="checkbox"
                                disabled={!canEdit}
                                checked={value.require_optin === 1}
                                onChange={(e) => change(team, { require_optin: e.target.checked ? 1 : 0 })}
                              />{' '}
                              Accepts automatic tickets{' '}
                              <i
                                className="fas fa-info-circle color-blue font-14"
                                style={{ cursor: 'default' }}
                                title="The Auto assign switch on each account: Settings > Users > the account > Privacy settings. Nobody without it gets a ticket from here."
                              ></i>
                            </label>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="checkbox">
                            <label>
                              <input
                                type="checkbox"
                                className="aaOnline"
                                disabled={!canEdit}
                                checked={value.only_online === 1}
                                onChange={(e) => change(team, { only_online: e.target.checked ? 1 : 0 })}
                              />{' '}
                              Signed in
                            </label>

                            {/* Fereastra continua chiar propozitia bifei: "Signed in - within 15
                                minutes". Pe rand separat ar intreba "cat timp" fara sa se vada la ce. */}
                            {value.only_online === 1 ? (
                              <span className="aaOnlineRow text-muted">
                                {' '}
                                within{' '}
                                <input
                                  type="number"
                                  className="form-control input-sm"
                                  min={1}
                                  max={1440}
                                  disabled={!canEdit}
                                  style={{ display: 'inline-block', width: 60, textAlign: 'center' }}
                                  value={value.online_minutes}
                                  onChange={(e) => change(team, { online_minutes: Number(e.target.value) })}
                                />{' '}
                                minutes
                              </span>
                            ) : null}

                            <i
                              className="fas fa-info-circle color-blue font-14"
                              style={{ cursor: 'default' }}
                              title="Counted from their last activity. A ticket given to somebody who has gone home looks handled, and nobody looks at it again."
                            ></i>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="checkbox">
                            <label>
                              <input
                                type="checkbox"
                                disabled={!canEdit}
                                checked={value.respect_work_hours === 1}
                                onChange={(e) => change(team, { respect_work_hours: e.target.checked ? 1 : 0 })}
                              />{' '}
                              Inside their work hours{' '}
                              <i
                                className="fas fa-info-circle color-blue font-14"
                                style={{ cursor: 'default' }}
                                title="The plan on each account, in Settings > Users > Work Hour Plans."
                              ></i>
                            </label>
                          </div>
                        </div>
                      </div>

                      <hr className="dotted" />

                      <div className="row">
                        <div className="col-md-12">
                          <label className="control-label">Office priority</label>
                          <div className="checkbox">
                            <label>
                              <input
                                type="checkbox"
                                disabled={!canEdit}
                                checked={value.prefer_office_responsible === 1}
                                onChange={(e) =>
                                  change(team, { prefer_office_responsible: e.target.checked ? 1 : 0 })
                                }
                              />{' '}
                              The person responsible for that office comes first
                            </label>
                          </div>
                          <span className="help-block">
                            Set per office in Settings &rsaquo; Customers &rsaquo; Offices, as{' '}
                            <em>ticket responsible</em>. It is a priority, not a filter: with nobody responsible for
                            that office in this team, the ticket still goes to somebody else here.
                            {team.updated_at ? (
                              <>
                                <br />
                                Last changed {team.updated_at.slice(0, 16)}
                              </>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
